import { useIntl } from "react-intl";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { SimpleTooltip, TableToolbar, SortArrows } from "@rio-cloud/rio-uikit";

import VehicleAlerts from "../../../../../components/vehicleAlerts";
import PeriodicReviews from "../../../../../components/periodicReviews";
import Inspections from "../../../../../components/inspections";
import ToSchedule from "./toSchedule";
import NextScheduling from "./nextScheduling";

import {
  Asset,
  AssetConnectStatus,
  AssetManualMetrics,
  AssetPlanList,
} from "../../../../../models/asset";

import { ManualDialog } from "../dialogs/manualDialog";
import { EditOrdometerHourmeterDialog } from "../dialogs/editOrdometerHourmeterDialog";

import {
  assetHasMetrics,
  assetHasOneMetric,
  getAssetRioPlan,
  getUptimeAsset,
} from "../../../../../services/assetService";

import {
  renderTableCaption,
  renderTableHead,
} from "../../../../../utils/tableUtils";
import { getFixedColumns, getScrollableColumns } from "./tableConfigs";

import useAssetAlert from "../../../../../hooks/useAssetAlert";
import useAssetCampaign from "../../../../../hooks/useAssetCampaign";
import useAssetPeriodicReview from "../../../../../hooks/useAssetPeriodicReviews";
import useAssetManuals from "../../../../../hooks/useAssetManuals";
import useTableScroll from "../../../../../hooks/useTableScroll";
import { useRioPlansCache } from "../../../../../hooks/useAssets";
import { useChassisCopy } from "../../../../../hooks/useChassisCopy";
import { useMediaQuery } from "@mui/material";

import {
  fetchMaintenance,
  IAssetSummary,
} from "../../../../../api/fetchMaintenance";
import { fetchComplementaryAssetsDetails } from "../../../../../api/fetchAssets";
import { fetchCheckupsByChassisList } from "../../../../../api/fetchCheckup";
import { fetchAssetsByRioPlans } from "../../../../../api/fetchBigNumbers";
import { exportAssetsToExcel } from "../../../../../utils/exportAssetsToExcel";
import ExportProgressDialog from "./exportProgressDialog";
import { calcProgress } from "../../../../../utils/exportProgress";
import "../styles.scss";
import { useRef, useEffect, useCallback, useLayoutEffect } from "react";
import { get } from "lodash";
import ContractDetailsSidebar from "../../../../../components/contractDetailsSidebar";

interface IMaintenanceTableProps {
  assets: Asset[];
  uptimeAssets: AssetManualMetrics[];
  assetsPlans: AssetPlanList[];
  assetsConnectionStatus: AssetConnectStatus[];
  availableToScheduleAssetIds: string[];
  allSummaryAssets: IAssetSummary[];
  isSidebarOpen: boolean;
  onAssetUpdate?: (chassis: string) => void;
}

const MaintenanceTable = (props: IMaintenanceTableProps) => {
  const intl = useIntl();
  const fixedColumns = getFixedColumns(intl);
  const scrollableColumns = getScrollableColumns(intl);
  const navigate = useNavigate();
  const [, setStateUpdatedAt] = useState(new Date().getTime());
  const [isExporting, setIsExporting] = useState(false);
  const [contractDetailsAsset, setContractDetailsAsset] = useState<Asset | null>(null);
  const [showContractDetailsDialog, setShowContractDetailsDialog] = useState(false);
  const [progressOpen, setProgressOpen] = useState(false);
  
  const handleAssetUpdate = useCallback((chassis: string) => {
    setStateUpdatedAt(new Date().getTime());
    if (props.onAssetUpdate) {
      props.onAssetUpdate(chassis);
    }
  }, [props.onAssetUpdate]);
  const [progressStage, setProgressStage] = useState<
    | "maintenance"
    | "complementary"
    | "checkups"
    | "rioplans"
    | "writing"
    | "done"
    | "error"
  >("maintenance");
  const [progressPct, setProgressPct] = useState(0);
  const [progressDetail, setProgressDetail] = useState<string | undefined>();
  const [progressError, setProgressError] = useState<string | undefined>();
  const { copyChassis, isCopied } = useChassisCopy();

  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const fixedTableHeaderRef = useRef<HTMLTableSectionElement>(null);

  const [showStickyScrollableHeader, setShowStickyScrollableHeader] = useState(false);
  const [scrollableHeaderScrollLeft, setScrollableHeaderScrollLeft] = useState(0);
  const scrollableTableHeaderRef = useRef<HTMLTableSectionElement>(null);
  const scrollableHeaderContainerRef = useRef<HTMLTableElement>(null);

  const [showResponsiveStickyHeader, setShowResponsiveStickyHeader] = useState(false);
  const responsiveTableHeaderRef = useRef<HTMLTableSectionElement>(null);
  const [responsiveHeaderScrollLeft, setResponsiveHeaderScrollLeft] = useState(0);
  const responsiveTableContainerRef = useRef<HTMLDivElement>(null);
  const responsiveScrollableRef = useRef<HTMLDivElement>(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const fixedTableContainerRef = useRef<HTMLDivElement>(null);
  const [fixedTableWidth, setFixedTableWidth] = useState(0);

  // Atualizar windowWidth quando a janela for redimensionada
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useLayoutEffect(() => {
    const el = fixedTableContainerRef.current;
    if (!el) return;
    const measureWidth = () => {
      const tableEl = el.querySelector<HTMLTableElement>('table');
      return Math.max(
        el.getBoundingClientRect().width,
        tableEl ? tableEl.getBoundingClientRect().width : 0
      );
    };
    const update = () => setFixedTableWidth(measureWidth());
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    const tableEl = el.querySelector('table');
    if (tableEl) observer.observe(tableEl);
    window.addEventListener('resize', update);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  useEffect(() => {
    const el = fixedTableContainerRef.current;
    if (!el) return;
    let rafId: number;
    const startTime = performance.now();
    const ANIM_DURATION = 450;
    const update = (now: number) => {
      const tableEl = el.querySelector<HTMLTableElement>('table');
      const width = Math.max(
        el.getBoundingClientRect().width,
        tableEl ? tableEl.getBoundingClientRect().width : 0
      );
      setFixedTableWidth(width);
      if (now - startTime < ANIM_DURATION) {
        rafId = requestAnimationFrame(update);
      }
    };
    rafId = requestAnimationFrame(update);
    return () => cancelAnimationFrame(rafId);
  }, [props.isSidebarOpen]);

  const isResponsiveView = useMediaQuery("(max-width: 1024px)");

  const getThresholds = useCallback(() => {
    return {
      show: -50,
      hide: 50,
    };
  }, []);

  const checkResponsiveHeaderPosition = useCallback(() => {
    if (!responsiveTableHeaderRef.current) return;

    const rect = responsiveTableHeaderRef.current.getBoundingClientRect();

    setShowResponsiveStickyHeader((prev) => {
      if (!prev && rect.top <= -10) {
        return true;
      } else if (prev && rect.top >= 10) {
        return false;
      }
      return prev;
    });
  }, []);

  const handleResponsiveScroll = useCallback(() => {
    if (showResponsiveStickyHeader && responsiveScrollableRef.current) {
      setResponsiveHeaderScrollLeft(responsiveScrollableRef.current.scrollLeft);
    }
  }, [showResponsiveStickyHeader]);

  useEffect(() => {
    const checkHeaderPosition = () => {
      if (!fixedTableHeaderRef.current) return;

      const rect = fixedTableHeaderRef.current.getBoundingClientRect();
      const { show, hide } = getThresholds();
      
      setShowStickyHeader((prev) => {
        if (!prev && rect.bottom <= show) {
          return true;
        } else if (prev && rect.bottom >= hide) {
          return false;
        }
        return prev;
      });
    };

    const handleScroll = () => {
      if (showStickyHeader && fixedTableHeaderRef.current) {
        const rect = fixedTableHeaderRef.current.getBoundingClientRect();
        const { hide } = getThresholds();
        if (rect.bottom >= hide) {
          setShowStickyHeader(false);
          return;
        }
      }
      checkHeaderPosition();
    };

    const findScrollableParent = (
      element: HTMLElement | null
    ): HTMLElement | null => {
      if (!element) return null;
      const style = window.getComputedStyle(element);
      const hasScroll =
        style.overflow === "auto" ||
        style.overflow === "scroll" ||
        style.overflowY === "auto" ||
        style.overflowY === "scroll";

      if (hasScroll && element.scrollHeight > element.clientHeight) {
        return element;
      }
      return findScrollableParent(element.parentElement);
    };

    const scrollableContainer = findScrollableParent(
      fixedTableHeaderRef.current?.parentElement || null
    );

    const elements: (Window | Document | HTMLElement)[] = [window, document];
    if (scrollableContainer) {
      elements.push(scrollableContainer as HTMLElement);
    }

    const commonScrollables = document.querySelectorAll(
      ".main-content, .page-content, .layout-content, [data-scroll='true']"
    );
    commonScrollables.forEach((el) => {
      el.addEventListener("scroll", handleScroll, { passive: true });
    });

    elements.forEach((element) => {
      element.addEventListener("scroll", handleScroll, { passive: true });
    });

    checkHeaderPosition();
    const interval = setInterval(() => {
      checkHeaderPosition();
    }, 2000);

    return () => {
      clearInterval(interval);
      elements.forEach((element) => {
        element.removeEventListener("scroll", handleScroll);
      });
      commonScrollables.forEach((el) => {
        el.removeEventListener("scroll", handleScroll);
      });
    };
  }, [showStickyHeader, getThresholds]);

  // Efeito para o header responsivo
  useEffect(() => {
    if (!isResponsiveView) return;

    const handleResponsiveScrollLocal = () => {
      if (showResponsiveStickyHeader && responsiveTableHeaderRef.current) {
        const rect = responsiveTableHeaderRef.current.getBoundingClientRect();
        if (rect.top >= 10) {
          setShowResponsiveStickyHeader(false);
          return;
        }
      }
      checkResponsiveHeaderPosition();
    };

    const elements: (Window | Document | HTMLElement)[] = [window, document];

    // Adicionar listener para scroll horizontal da tabela responsiva
    if (responsiveScrollableRef.current) {
      responsiveScrollableRef.current.addEventListener('scroll', handleResponsiveScroll, { passive: true });
    }

    const commonScrollables = document.querySelectorAll(
      ".main-content, .page-content, .layout-content, [data-scroll='true']"
    );
    commonScrollables.forEach((el) => {
      el.addEventListener("scroll", handleResponsiveScrollLocal, { passive: true });
    });

    elements.forEach((element) => {
      element.addEventListener("scroll", handleResponsiveScrollLocal, { passive: true });
    });

    checkResponsiveHeaderPosition();
    const interval = setInterval(() => {
      checkResponsiveHeaderPosition();
    }, 2000);

    return () => {
      clearInterval(interval);
      elements.forEach((element) => {
        element.removeEventListener("scroll", handleResponsiveScrollLocal);
      });
      commonScrollables.forEach((el) => {
        el.removeEventListener("scroll", handleResponsiveScrollLocal);
      });
      if (responsiveScrollableRef.current) {
        responsiveScrollableRef.current.removeEventListener('scroll', handleResponsiveScroll);
      }
    };
  }, [isResponsiveView, showResponsiveStickyHeader, checkResponsiveHeaderPosition, handleResponsiveScroll]);

  const checkScrollableHeaderPosition = useCallback(() => {
    
    if (!scrollableTableHeaderRef.current) {
      return;
    }
    
    const rect = scrollableTableHeaderRef.current.getBoundingClientRect();
    const { show, hide } = getThresholds();
    
    
    
    setShowStickyScrollableHeader((prev) => {
      if (!prev && rect.bottom <= show) {
        return true;
      } else if (prev && rect.bottom >= hide) {
        return false;
      }
      return prev;
    });
    
    if (showStickyScrollableHeader) {
      const scrollableTable = document.querySelector('.original-scrollable-table');
      if (scrollableTable) {
        setScrollableHeaderScrollLeft(scrollableTable.scrollLeft || 0);
      }
    }
  }, [showStickyScrollableHeader, getThresholds]);

  const handleScrollableScroll = useCallback(() => {
    checkScrollableHeaderPosition();
    
    const scrollableTable = document.querySelector('.original-scrollable-table') as HTMLElement;
    if (scrollableTable) {
      setScrollableHeaderScrollLeft(scrollableTable.scrollLeft);
    }
  }, [checkScrollableHeaderPosition]);

  useLayoutEffect(() => {
    const findScrollableParent = (
      element: HTMLElement | null
    ): HTMLElement | null => {
      if (!element) return null;
      const style = window.getComputedStyle(element);
      const hasScroll =
        style.overflow === "auto" ||
        style.overflow === "scroll" ||
        style.overflowY === "auto" ||
        style.overflowY === "scroll";

      if (hasScroll && element.scrollHeight > element.clientHeight) {
        return element;
      }
      return findScrollableParent(element.parentElement);
    };

    const scrollableContainer = findScrollableParent(
      scrollableTableHeaderRef.current?.parentElement || null
    );
    

    const elements: (Window | Document | HTMLElement)[] = [window, document];
    if (scrollableContainer) {
      elements.push(scrollableContainer as HTMLElement);
    }

    const scrollableTable = document.querySelector('.original-scrollable-table') as HTMLElement;
    if (scrollableTable) {
      scrollableTable.addEventListener('scroll', handleScrollableScroll, { passive: true });
    }

    const commonScrollables = document.querySelectorAll(
      ".main-content, .page-content, .layout-content, [data-scroll='true']"
    );
    commonScrollables.forEach((el) => {
      el.addEventListener("scroll", handleScrollableScroll, { passive: true });
    });

    elements.forEach((element) => {
      element.addEventListener("scroll", handleScrollableScroll, { passive: true });
    });

    checkScrollableHeaderPosition();
    const interval = setInterval(() => {
      checkScrollableHeaderPosition();
    }, 2000);

    return () => {
      clearInterval(interval);
      elements.forEach((element) => {
        element.removeEventListener("scroll", handleScrollableScroll);
      });
      commonScrollables.forEach((el) => {
        el.removeEventListener("scroll", handleScrollableScroll);
      });
      if (scrollableTable) {
        scrollableTable.removeEventListener('scroll', handleScrollableScroll);
      }
    };
  }, [handleScrollableScroll, checkScrollableHeaderPosition]);

  useEffect(() => {
    const handleResponsiveScroll = () => {
      if (showResponsiveStickyHeader && responsiveTableHeaderRef.current) {
        const rect = responsiveTableHeaderRef.current.getBoundingClientRect();
        if (rect.top >= 10) {
          setShowResponsiveStickyHeader(false);
          return;
        }
      }
      checkResponsiveHeaderPosition();
    };

    const findScrollableParent = (
      element: HTMLElement | null
    ): HTMLElement | null => {
      if (!element) return null;
      const style = window.getComputedStyle(element);
      const hasScroll =
        style.overflow === "auto" ||
        style.overflow === "scroll" ||
        style.overflowY === "auto" ||
        style.overflowY === "scroll";

      if (hasScroll && element.scrollHeight > element.clientHeight) {
        return element;
      }
      return findScrollableParent(element.parentElement);
    };

    const scrollableContainer = findScrollableParent(
      responsiveTableHeaderRef.current?.parentElement || null
    );

    const elements: (Window | Document | HTMLElement)[] = [window, document];
    if (scrollableContainer) {
      elements.push(scrollableContainer as HTMLElement);
    }

    // Adicionar listener para scroll horizontal da tabela responsiva
    const responsiveTableContainer = document.querySelector('.tables-row.overflow-x-auto') as HTMLElement;
    if (responsiveTableContainer) {
      responsiveTableContainer.addEventListener('scroll', handleResponsiveScroll, { passive: true });
    }

    const commonScrollables = document.querySelectorAll(
      ".main-content, .page-content, .layout-content, [data-scroll='true']"
    );
    commonScrollables.forEach((el) => {
      el.addEventListener("scroll", handleResponsiveScroll, { passive: true });
    });

    elements.forEach((element) => {
      element.addEventListener("scroll", handleResponsiveScroll, { passive: true });
    });

    checkResponsiveHeaderPosition();
    const interval = setInterval(() => {
      checkResponsiveHeaderPosition();
    }, 2000);

    return () => {
      clearInterval(interval);
      elements.forEach((element) => {
        element.removeEventListener("scroll", handleResponsiveScroll);
      });
      commonScrollables.forEach((el) => {
        el.removeEventListener("scroll", handleResponsiveScroll);
      });
      if (responsiveTableContainer) {
        responsiveTableContainer.removeEventListener('scroll', handleResponsiveScroll);
      }
    };
  }, [showResponsiveStickyHeader, checkResponsiveHeaderPosition]);

  // Calcula margin-left da tabela scrollável considerando sidebar aberta/fechada
  const getScrollableTableMargin = (width: number, isSidebarOpen: boolean): string => {
    const TABLE_GAP = 24; // Gap fixo em pixels
    
    // Mobile - <= 768px
    if (width <= 768) {
      return isSidebarOpen ? `calc(78% + ${TABLE_GAP}px)` : `calc(67% + ${TABLE_GAP}px)`;
    }
    // Tablet - 769px a 1024px
    if (width >= 769 && width <= 1024) {
      return isSidebarOpen ? `calc(75% + ${TABLE_GAP}px)` : `calc(67% + ${TABLE_GAP}px)`;
    }
    // Laptop pequeno - 1025px a 1280px
    if (width >= 1025 && width <= 1280) {
      return isSidebarOpen ? `calc(73% + ${TABLE_GAP}px)` : `calc(65% + ${TABLE_GAP}px)`;
    }
    // Laptop médio - 1281px a 1366px
    if (width >= 1281 && width <= 1366) {
      return isSidebarOpen ? `calc(56.5% + ${TABLE_GAP}px)` : `calc(56.5% + ${TABLE_GAP}px)`;
    }
    // Laptop grande - 1367px a 1440px
    if (width >= 1367 && width <= 1440) {
      return isSidebarOpen ? `calc(51% + ${TABLE_GAP}px)` : `calc(51% + ${TABLE_GAP}px)`;
    }
    // Desktop médio - 1441px a 1600px
    if (width >= 1441 && width <= 1600) {
      return isSidebarOpen ? `calc(79.4% + ${TABLE_GAP}px)` : `calc(59% + ${TABLE_GAP}px)`;
    }
    // Desktop Full HD - 1601px a 1920px
    if (width >= 1601 && width <= 1919) {
      return isSidebarOpen ? `calc(68.9% + ${TABLE_GAP}px)` : `calc(53% + ${TABLE_GAP}px)`;
    }
    // Desktop 2K - 1921px a 2560px
    if (width >= 1920 && width <= 2560) {
      return isSidebarOpen ? `calc(50.6% + ${TABLE_GAP}px)` : `calc(50.6% + ${TABLE_GAP}px)`;
    }
    // Desktop 4K+ - > 2560px
    return isSidebarOpen ? `calc(57% + ${TABLE_GAP}px)` : `calc(52% + ${TABLE_GAP}px)`;
  };

  const getStickyHeaderWidth = (width: number, isSidebarOpen: boolean): string => {
    if (width <= 1366) {
      return isSidebarOpen ? "76.5%" : "104%"; 
    }
    if(width >= 1440 && width <= 1600) {
      return isSidebarOpen ? "71%" : "95%"; 
    }
    if (width >= 1601 && width <= 1920) {
      return isSidebarOpen ? "77.7%" : "95.6%"; 
    }
    if (width >= 1921 && width <= 2560) {
      return isSidebarOpen ? "82.7%" : "96.2%"; 
    }
    return "99%"; 
  };

  const exportToExcel = async () => {
    setProgressOpen(true);
    setProgressError(undefined);
    setProgressStage("maintenance");
    setProgressPct(0);

    try {
      await exportAssetsToExcel({
        allSummaryAssets: props.allSummaryAssets,
        assets: props.assets,
        assetsPlans: props.assetsPlans,
        assetsConnectionStatus: props.assetsConnectionStatus,
        fetchMaintenance,
        fetchComplementaryAssetsDetails,
        fetchCheckupsByChassisList,
        fetchAssetsByRioPlans, // << use a função que você mostrou
        getAssetRioPlan, // fallback, se quiser
        onChunk: (stage, index, total) => {
          // converte o stage do util pro nosso tipo
          const s = stage as any;
          setProgressStage(s);
          setProgressDetail(total ? `${index}/${total}` : undefined);
          const pct = calcProgress(
            s === "rioplans" ||
              s === "writing" ||
              s === "maintenance" ||
              s === "complementary" ||
              s === "checkups"
              ? s
              : "writing",
            Math.min(index, total),
            total || 1
          );
          setProgressPct(pct);
        },
      });
      setProgressPct(100);
      setProgressStage("done");
      setProgressDetail(undefined);
    } catch (err: any) {
      setProgressStage("error");
      setProgressError(err?.message || "Erro ao exportar");
    }
  };

  const {
    hasAlert,
    hasCriticalAlert,
    getCriticalAlertCount,
    handleAlertSidebar,
    handleCloseAlertSidebar,
    alertSidebar,
    assetAlerts,
  } = useAssetAlert(props.assets.map((asset) => asset.id));
  const {
    assetManuals,
    handleAssetManualsSidebar,
    handleCloseAssetManualsSidebar,
    assetManualsDialog,
    assetManualsLoading,
    assetManualsChassis,
  } = useAssetManuals();
  const {
    hasCampaign,
    handleCampaignsSidebar,
    handleCloseCampaignSidebar,
    campaignSidebar,
    campaigns,
    campaignsAssetId,
  } = useAssetCampaign();
  const {
    handlePeriodicReviewSidebar,
    handleClosePeriodicReviewSidebar,
    periodicReviewSidebar,
    assetPeriodicReviews,
  } = useAssetPeriodicReview();
  const { scrolledClass, scrollableTableRef } = useTableScroll();

  useLayoutEffect(() => {
    const fixedContainer = fixedTableContainerRef.current;
    const scrollableContainer = scrollableTableRef.current;
    if (!fixedContainer || !scrollableContainer) return;

    const fixedRows = Array.from(fixedContainer.querySelectorAll<HTMLElement>('tbody tr'));
    const scrollableRows = Array.from(scrollableContainer.querySelectorAll<HTMLElement>('tbody tr'));
    if (!fixedRows.length || !scrollableRows.length) return;

    // Reset inline heights before measuring so natural content heights are used
    [...fixedRows, ...scrollableRows].forEach(row => { row.style.height = ''; });

    const count = Math.min(fixedRows.length, scrollableRows.length);
    for (let i = 0; i < count; i++) {
      const h = Math.max(
        fixedRows[i].getBoundingClientRect().height,
        scrollableRows[i].getBoundingClientRect().height
      );
      fixedRows[i].style.height = `${h}px`;
      scrollableRows[i].style.height = `${h}px`;
    }
  }, [props.assets, scrollableTableRef]);

  const renderSchedulingData = (columnValue: string, asset: Asset) => {
    return (
      columnValue === "agendamento" &&
      asset.schedulings?.previous && (
        <ToSchedule
          asset={asset}
          hasMetrics={
            assetHasOneMetric(asset, props.uptimeAssets) &&
            props.availableToScheduleAssetIds.includes(asset.id)
          }
        />
      )
    );
  };

  const renderChassisData = (columnValue: string, asset: Asset) => {
    const handleChassisClick = async () => {
      // Copiar automaticamente o chassis para a área de transferência
      await copyChassis(asset.chassis);
      
      // Abrir o sidebar de manuais
      handleAssetManualsSidebar(asset.chassis);
    };

    return (
      columnValue === "chassi" && (
        <div className="chassi-info relative">
          <button
            type="button"
            onClick={handleChassisClick}
            className="flex items-center gap-2"
          >
            <span className="chassi-info">{asset.chassis}</span>
          </button>
          
          {isCopied(asset.chassis) && (
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-green-600 text-white text-xs rounded shadow-lg z-10 whitespace-nowrap">
              Item Copiado
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-green-600"></div>
            </div>
          )}
        </div>
      )
    );
  };

  const renderOdometerData = (columnValue: string, asset: Asset) => {
    const lastOdometerRevision = asset.schedulings?.previous
      ?.filter(s => (s.odometer ?? 0) > 0)
      ?.sort((a, b) => (b?.odometer ?? 0) - (a?.odometer ?? 0))?.[0];

    
    return (
      columnValue === "odometro" && (
        <div className="text-center">
          {asset.odometer ? (
            <div>
              <div className="truncate">
                {asset.odometer.toLocaleString("pt-BR") + " km"}
              </div>
              <SimpleTooltip
                content={`${intl.formatMessage({ id: "intl-msg:monitoring.table.updatedAt" })} ${asset?.lastOdometerUpdate?.toLocaleDateString(
                  "pt-BR"
                )}`}
              >
                <div className="text-size-11 margin-top-5 text-color-highlight line-clamp-2">
                  {asset?.lastOdometerUpdate
                    ? `${intl.formatMessage({ id: "intl-msg:monitoring.table.updatedAt" })} ${asset?.lastOdometerUpdate?.toLocaleDateString(
                        "pt-BR"
                      )}`
                    : ""}
                </div>
              </SimpleTooltip>
            </div>
          ) : getUptimeAsset(asset, props.uptimeAssets)?.odometer ? (
            <div>
              <div className="truncate">
                {getUptimeAsset(
                  asset,
                  props.uptimeAssets
                )?.odometer?.toLocaleString("pt-BR") + " km"}
                <span className="margin-left-10">
                    <EditOrdometerHourmeterDialog
                      type="odometer"
                      asset={asset}
                      onClose={handleAssetUpdate}
                    />
                  </span>
              </div>
              {getUptimeAsset(asset, props.uptimeAssets)?.lastOdometerUpdate ? (
                <SimpleTooltip
                  content={`${intl.formatMessage({ id: "intl-msg:monitoring.table.updatedAt" })} ${getUptimeAsset(
                    asset,
                    props.uptimeAssets
                  )?.lastOdometerUpdate?.toLocaleDateString("pt-BR")}`}
                >
                  <div className="text-size-11 margin-top-5 text-color-highlight line-clamp-2">
                    {intl.formatMessage({ id: "intl-msg:monitoring.table.updatedAt" })}{" "}
                    {getUptimeAsset(
                      asset,
                      props.uptimeAssets
                    )?.lastOdometerUpdate?.toLocaleDateString("pt-BR")}
                  </div>
                </SimpleTooltip>
              ) : null}
            </div>
          ) : lastOdometerRevision?.odometer ? (
            <div>
              <div className="truncate">
                {lastOdometerRevision.odometer.toLocaleString("pt-BR") + " km"}
                <span className="margin-left-10">
                  <EditOrdometerHourmeterDialog
                    type="odometer"
                    asset={asset}
                    onClose={handleAssetUpdate}
                  />
                </span>
              </div>
              {lastOdometerRevision.date ? (
                <SimpleTooltip
                  content={`${intl.formatMessage({ id: "intl-msg:monitoring.table.updatedAt" })} ${lastOdometerRevision.date?.toLocaleDateString("pt-BR")}`}
                >
                  <div className="text-size-11 margin-top-5 text-color-highlight line-clamp-2">
                    {intl.formatMessage({ id: "intl-msg:monitoring.table.updatedAt" })}{" "}
                    {lastOdometerRevision.date?.toLocaleDateString("pt-BR")}
                  </div>
                </SimpleTooltip>
              ) : null}
            </div>
          ) : (
            <EditOrdometerHourmeterDialog
                type="odometer"
                asset={asset}
                onClose={handleAssetUpdate}
              />
          )}
        </div>
      )
    );
  };

  const renderHourmeterData = (columnValue: string, asset: Asset) => {
    const lastHourmeterRevision = asset.schedulings?.previous
      ?.filter(s => (s.hourmeter ?? 0) > 0)
      ?.sort((a, b) => (b?.hourmeter ?? 0) - (a?.hourmeter ?? 0))?.[0];

    return (
      columnValue === "horimetro" && (
        <div className="text-center">
          {asset.hourmeter ? (
            <div>
              <div className="truncate">
                {asset.hourmeter.toLocaleString("pt-BR") + " h"}
              </div>
              <SimpleTooltip
                content={`${intl.formatMessage({ id: "intl-msg:monitoring.table.updatedAt" })} ${asset?.lastHourmeterUpdate?.toLocaleDateString(
                  "pt-BR"
                )}`}
              >
                <div className="text-size-11 margin-top-5 text-color-highlight line-clamp-2">
                  {asset?.lastHourmeterUpdate
                    ? `${intl.formatMessage({ id: "intl-msg:monitoring.table.updatedAt" })} ${asset?.lastHourmeterUpdate?.toLocaleDateString(
                        "pt-BR"
                      )}`
                    : ""}
                </div>
              </SimpleTooltip>
            </div>
          ) : getUptimeAsset(asset, props.uptimeAssets)?.hourmeter ? (
            <div>
              <div className="truncate">
                {getUptimeAsset(
                  asset,
                  props.uptimeAssets
                )?.hourmeter?.toLocaleString("pt-BR") + " h"}
                <span className="margin-left-10">
                    <EditOrdometerHourmeterDialog
                      type="hourmeter"
                      asset={asset}
                      onClose={handleAssetUpdate}
                    />
                  </span>
              </div>
              {getUptimeAsset(asset, props.uptimeAssets)
                ?.lastHourmeterUpdate ? (
                <SimpleTooltip
                  content={`${intl.formatMessage({ id: "intl-msg:monitoring.table.updatedAt" })} ${getUptimeAsset(
                    asset,
                    props.uptimeAssets
                  )?.lastHourmeterUpdate?.toLocaleDateString("pt-BR")}`}
                >
                  <div className="text-size-11 margin-top-5 text-color-highlight line-clamp-2">
                    {intl.formatMessage({ id: "intl-msg:monitoring.table.updatedAt" })}{" "}
                    {getUptimeAsset(
                      asset,
                      props.uptimeAssets
                    )?.lastHourmeterUpdate?.toLocaleDateString("pt-BR")}
                  </div>
                </SimpleTooltip>
              ) : null}
            </div>
          ) : lastHourmeterRevision?.hourmeter ? (
            <div>
              <div className="truncate">
                {lastHourmeterRevision.hourmeter.toLocaleString("pt-BR") + " h"}
                <span className="margin-left-10">
                  <EditOrdometerHourmeterDialog
                    type="hourmeter"
                    asset={asset}
                    onClose={handleAssetUpdate}
                  />
                </span>
              </div>
              {lastHourmeterRevision.date ? (
                <SimpleTooltip
                  content={`${intl.formatMessage({ id: "intl-msg:monitoring.table.updatedAt" })} ${lastHourmeterRevision.date?.toLocaleDateString("pt-BR")}`}
                >
                  <div className="text-size-11 margin-top-5 text-color-highlight line-clamp-2">
                    {intl.formatMessage({ id: "intl-msg:monitoring.table.updatedAt" })}{" "}
                    {lastHourmeterRevision.date?.toLocaleDateString("pt-BR")}
                  </div>
                </SimpleTooltip>
              ) : null}
            </div>
          ) : (
            <EditOrdometerHourmeterDialog
                type="hourmeter"
                asset={asset}
                onClose={handleAssetUpdate}
              />
          )}
        </div>
      )
    );
  };

  const getLastReview = (asset: Asset) => {
    if (!asset.schedulings?.previous || asset.schedulings.previous.length === 0) {
      return null;
    }
    const reviewsWithData = asset.schedulings.previous
      .filter(review => review.date && review.checkup?.name)
      .sort((a, b) => new Date(b.date!).getTime() - new Date(a.date!).getTime());
    return reviewsWithData[0] || null;
  };

  const renderRioPlan = (asset: Asset) => {
    let plans;

    const cache = useRioPlansCache.getState().cache;
    if (Object.keys(cache).length !== 0) {
      const plansList = [];
      for (const [cacheKey, cachedData] of Object.entries(cache)) {
        if (!useRioPlansCache.getState().isCacheValid(cacheKey)) continue;
        const plansData = cachedData.data;
        for (const [planName, assetIds] of Object.entries(plansData)) {
          if (assetIds.includes(asset.id)) plansList.push(planName);
        }
      }
      plans = plansList.join(', ');
    }

    return <div className="text-center">
      {plans ? (
        <span className="text-color-gray line-clamp-2">{plans}</span>
      ) : (
        <button
          type="button"
          className="btn btn-primary"
          onClick={() =>
            window.open(
              "https://latam-marketplace.rio.cloud/customerCenter/services",
              "_blank"
            )
          }
        >
          <span
            className="rioglyph rioglyph-shopping-cart"
            aria-hidden="true"
          ></span>
        </button>
      )}
    </div>

  };

  const renderStickyScrollableHeader = () => {
    if (!showStickyScrollableHeader) {
      return null;
    }
    
    const getScrollableLeft = (): number => {
      const left = fixedTableContainerRef.current?.getBoundingClientRect().left ?? 0;
      return left + fixedTableWidth;
    };
    return (
      <div
        ref={scrollableHeaderContainerRef}
        className="scrollable-sticky-header"
        style={{
          position: 'fixed',
          top: 50,
          zIndex: 5,
          left: getScrollableLeft(),
          right: 28,
          display: 'block',
          opacity: 1,
          transition: 'opacity 0.2s ease-out',
          overflow: 'hidden'
        }}
      >
        <div 
          className="scrollabe-table"
          style={{
            transform: `translateX(${-scrollableHeaderScrollLeft}px)`,
            overflow: 'visible',
            marginLeft: 8,
            width: 'auto'
          }}
        >
          <table
            className="table table-layout-fixed table-column-overflow-hidden table-bordered table-head-filled"
            style={{ 
              minWidth: `${scrollableColumns.reduce((sum, col) => sum + (col.details?.width || col.details?.defaultWidth || 100), 0)}px`, 
              width: `${scrollableColumns.reduce((sum, col) => sum + (col.details?.width || col.details?.defaultWidth || 100), 0)}px`,
              tableLayout: 'fixed', 
              borderCollapse: 'collapse' 
            }}
          >
            <colgroup>
              {scrollableColumns.map((column) =>
                renderTableCaption(column.value, column.details)
              )}
            </colgroup>
            <thead>
              <tr>
                {scrollableColumns.map((column) => {
                  const colWidth = column.details?.width || column.details?.defaultWidth || 100;
                  return (
                    <th
                      key={column.value}
                      data-column={column.value}
                      className="user-select-none sort-column"
                      data-field={column.value}
                      title={column.label}
                      style={{ 
                        width: `${colWidth}px`,
                        minWidth: `${colWidth}px`,
                        maxWidth: `${colWidth}px`,
                        textAlign: column.value === "rio" ? "center" : undefined 
                      }}
                    >
                      <span>
                        <SortArrows />
                        <span>{column.label}</span>
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
          </table>
        </div>
      </div>
    );
  };

  const renderResponsiveStickyHeader = () => {
    if (!showResponsiveStickyHeader) return null;
    
    // Calcular posição left considerando sidebar aberta/fechada
    const getResponsiveLeft = (width: number, isSidebarOpen: boolean): string => {
      // Mobile - <= 768px
      if (width <= 375) {
        return  isSidebarOpen ? '3%' : '2%';
      }
      // Mobile pequeno - 376px a 480px
      if (width >= 376 && width <= 480) {
        return  isSidebarOpen ? '100%' : '15.5%';
      }
      // Mobile médio - 481px a 640px
      if (width >= 481 && width <= 640) {
        return  isSidebarOpen ? '40%' : '13%';
      }
      // Mobile grande - 641px a 768px
      if (width >= 641 && width <= 768) {
        return  isSidebarOpen ? '56%' : '10.5%';
      }
      // Tablet pequeno - 769px a 834px
      if (width >= 769 && width <= 834) {
        return  isSidebarOpen ? '53%' : '9.8%';
      }
      // Tablet médio - 835px a 960px
      if (width >= 835 && width <= 960) {
        return  isSidebarOpen ? '51%' : '9%';
      }
      // Tablet grande - 961px a 1024px
      if (width >= 961 && width <= 1024) {
        return  isSidebarOpen ? '42%' : '8%';
      }
      // Default (não deveria acontecer em responsive view, mas por segurança)
      return  isSidebarOpen ? '5%' : '3%';
    
    };

    const leftPosition = getResponsiveLeft(windowWidth, props.isSidebarOpen);

    return (
    <div
      className="responsive-sticky-header sticky z-10 bg-white border-b border-gray-200 "
      style={{
        marginBottom: '-1px',
        left: leftPosition,
        right: 28,
        top: 50,
        position: 'fixed',
        zIndex: 6,
        overflowX: 'hidden',
        boxSizing: 'border-box'
      }}
    >
        <div style={{ transform: `translateX(${-responsiveHeaderScrollLeft}px)` }}>
          <table className="table table-head-filled table-bordered min-w-full">
          <colgroup>
          {fixedColumns.map((column) =>
            renderTableCaption(column.value, column.details)
          )}
          {scrollableColumns.map((column) =>
             renderTableCaption(column.value, column.details)
          )}
          </colgroup>
            <thead>
              <tr>
                {fixedColumns.map((column) => renderTableHead(
                  column.value,
                  column.label,
                  "veiculo",
                  () => { }
                )
              )}
              {scrollableColumns.map((column) => renderTableHead(
                column.value,
                column.label,
                "veiculo",
                () => { }
              )
              )}
              </tr>
            </thead>
          </table>
        </div>
      </div>
    );
  };

  const renderModals = () => (
    <>
      {alertSidebar && (
        <VehicleAlerts
          alerts={assetAlerts}
          closeSidebar={handleCloseAlertSidebar}
        />
      )}

      <ManualDialog
        show={assetManualsDialog}
        chassis={assetManualsChassis}
        close={handleCloseAssetManualsSidebar}
        manuals={assetManuals}
        loading={assetManualsLoading}
      />

      {campaignSidebar && (
        <Inspections
          inspectionsData={{ list: campaigns?.list }}
          closeSidebar={handleCloseCampaignSidebar}
          assetId={campaignsAssetId!}
          userRole="TOWER"
        />
      )}

      {(() => {
        return periodicReviewSidebar && (
          <PeriodicReviews
            dialog={periodicReviewSidebar}
            asset={assetPeriodicReviews}
            closeSidebar={handleClosePeriodicReviewSidebar}
            scheduleIds={[
              ...(assetPeriodicReviews?.schedulings?.next?.map(
                (review) => review.id
              ) ?? []),
              ...(assetPeriodicReviews?.schedulings?.previous?.map(
                (review) => review.id
              ) ?? []),
            ]}
            userRole="TOWER"
          />
        );
      })()}

      <ExportProgressDialog
        show={progressOpen}
        stage={progressStage}
        progress={progressPct}
        detail={progressDetail}
        errorMessage={progressError}
        onClose={() => setProgressOpen(false)}
      />

      <ContractDetailsSidebar
        asset={contractDetailsAsset}
        show={showContractDetailsDialog}
        onClose={() => {
          setShowContractDetailsDialog(false);
          setContractDetailsAsset(null);
        }}
      />
    </>
  );


  if (isResponsiveView) {
    return (
      <div id="TableCommonDemo">
        <TableToolbar>
          <div className="table-toolbar-container">
            <div className="table-toolbar-group-right">
              <div className="table-toolbar-column table-toolbar-column-spacer">
                <div className="grid grid-cols-3 gap-6 w-full">
                  <button
                    type="button"
                    className="btn bg-highlight-darker p-2 col-span-2"
                    onClick={() => navigate(`/dealership/appointments/appointment`)}
                  >
                    <span className="text-capitalize padding-right-5">
                      {intl.formatMessage({ id: "intl-msg:monitoring.createScheduling" })}
                    </span>
                    <span
                      className="rioglyph rioglyph-plus-sign"
                      aria-hidden="true" />
                  </button>

                  <button
                    className="btn bg-highlight-darker flex items-center justify-center"
                    onClick={exportToExcel}
                    disabled={!props.assets?.length}
                  >
                    <span className="rioglyph rioglyph-download !text-[20px] !m-[0]"></span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </TableToolbar>
        {renderResponsiveStickyHeader()}
        <div ref={responsiveScrollableRef} className="tables-row overflow-x-auto w-full">
          <table className="table table-head-filled table-bordered min-w-full">
            <colgroup>
                {fixedColumns.map((column) =>
                  renderTableCaption(column.value, column.details)
                  )}
                  {scrollableColumns.map((column) =>
                    renderTableCaption(column.value, column.details)
                  )}
            </colgroup>
            <thead ref={responsiveTableHeaderRef}>
              <tr>
                {fixedColumns.map((column) => renderTableHead(
                  column.value,
                  column.label,
                  "veiculo",
                  () => { }
                )
              )}
              {scrollableColumns.map((column) =>
                renderTableHead(
                  column.value,
                  column.label,
                  "veiculo",
                  () => { }
                )
                )}
              </tr>
            </thead>
            <tbody>
              {props.assets.map((asset, assetIndex) => (
                <tr key={assetIndex}>
                  {fixedColumns.map((column, colIndex) => (
                    <td key={`${column.value}-${assetIndex}-${colIndex}`} data-field={column.label}>
                      {renderSchedulingData(column.value, asset)}

                      {column.value === "veiculo" && (
                        <div className="warning-vehicle">
                          <span className="vehicle-name" title={asset.name}>
                            {asset.name ?? asset.model}
                          </span>
                          {hasAlert(asset.id) ? (
                            <button
                              type="button"
                              className="btn btn-secondary btn-action text-size-h6"
                              onClick={() => handleAlertSidebar(asset.id)}
                            >
                              <span
                                className="rioglyph rioglyph-warning-sign"
                                aria-hidden="true"
                              ></span>
                            </button>
                          ) : null}
                        </div>
                      )}

                      {renderChassisData(column.value, asset)}
                      {renderOdometerData(column.value, asset)}
                      {renderHourmeterData(column.value, asset)}

                      {column.value === "licensePlate" && (
                        <div>
                          {asset.licensePlate === '-' ? (
                            <span>-</span> 
                          ) : asset.licensePlate ? (
                            <SimpleTooltip
                              content={asset.licensePlate}
                              placement="top"
                            >
                              <div className="truncate">{asset.licensePlate}</div>
                            </SimpleTooltip>
                          ) : (
                            <div className="animate-pulse">
                            <div className="w-3/4 h-4 bg-gray-300 rounded" />
                          </div>
                          )}
                        </div>
                      )}

                      {column.value === "accountName" && (
                        asset.accountName ? (
                          <SimpleTooltip content={asset.accountName} placement="top">
                            <div className="line-clamp-3">{asset.accountName}</div>
                          </SimpleTooltip>
                        ) : (
                          <div className="animate-pulse">
                            <div className="w-3/4 h-4 bg-gray-300 rounded" />
                          </div>
                        )
                      )}
                    </td>
                  ))}
                  {scrollableColumns.map((column, colIndex) => (
                    <td key={`${column.value}-${assetIndex}-${colIndex}`} data-field={column.label}>
                      {renderChassisData(column.value, asset)}
                      {renderOdometerData(column.value, asset)}
                      {renderHourmeterData(column.value, asset)}

                      {column.value === "acoes" && (
                        <div className="acoes-oficina text-center display-flex items-center justify-center gap-2">
                          {hasCriticalAlert(asset.id) && (
                            <SimpleTooltip content={`${getCriticalAlertCount(asset.id)} alerta(s) crítico(s)`}>
                              <span 
                                className="rioglyph rioglyph-warning-sign text-color-danger cursor-pointer" 
                                style={{ fontSize: '18px' }}
                                onClick={() => handleAlertSidebar(asset.id)}
                              />
                            </SimpleTooltip>
                          )}
                          {hasCampaign(asset) ? (
                            <div className="cursor-pointer relative" onClick={() => handleCampaignsSidebar(asset)}>
                              <span className="rioglyph rioglyph-calendar-today" />
                              {(asset?.campaigns?.number ?? 0) > 0 && (
                                <span className="alert-info">
                                  {asset?.campaigns?.number}
                                </span>
                              )}
                            </div>
                          ) : (
                            <div>
                              <span className="rioglyph rioglyph-calendar-today" />
                            </div>
                          )}
                        </div>
                      )}
                      {column.value === "revisoes" && (
                        asset.schedulings?.previous ? (
                          <div className="revisoes-periodicas text-center">
                            <button
                              type="button"
                              className="btn btn-primary btn-action text-size-22"
                              onClick={() => handlePeriodicReviewSidebar(asset)}
                            >
                              <span
                                className="rioglyph rioglyph-book"
                                aria-hidden="true"
                              ></span>
                            </button>
                          </div>
                        ) : (
                          <div className="animate-pulse">
                            <div className="w-3/4 h-4 bg-gray-300 rounded" />
                          </div>
                        )
                      )}

                      {column.value === "score" && (
                        <div className="text-center text-color-light">-</div>
                      )}

                      {column.value === "grupoManutencao" && (
                        <div className="text-center">{asset.groupName}</div>
                      )}

                      {column.value === "status" && (
                        <div className="truncate">
                          <span
                            className={["Online", "Conectado"].includes(
                              props.assetsConnectionStatus.find(
                                (el) => el.assetId === asset.id
                              )?.connection ?? ""
                            )
                              ? "rioglyph rioglyph-wifi text-color-status-available padding-right-5"
                              : ["Offline", "Desconectado"].includes(
                                props.assetsConnectionStatus.find(
                                  (el) => el.assetId === asset.id
                                )?.connection ?? ""
                              )
                                ? "rioglyph rioglyph-wifi-off padding-right-5"
                                : undefined}
                          ></span>
                          {["Online", "Conectado"].includes(
                            props.assetsConnectionStatus.find(
                              (el) => el.assetId === asset.id
                            )?.connection ?? ""
                          )
                            ? intl.formatMessage({ id: "intl-msg:monitoring.status.connected" })
                            : intl.formatMessage({ id: "intl-msg:monitoring.status.notConnected" })}
                        </div>
                      )}

                      {column.value === "rio" && renderRioPlan(asset)}

                      {column.value === "statusGarantia" && (
                        <div className="status-garantia display-grid place-items-center">
                          <span
                            className={[
                              "Em Garantia",
                              "In Warranty",
                              "En garantia",
                              "En garantía",
                            ].includes(asset.warrantyStatus ?? "")
                              ? "text-color-garantia"
                              : "text-color-expirada"}
                          >
                            {[
                              "Em Garantia",
                              "In Warranty",
                              "En garantia",
                              "En garantía",
                            ].includes(asset.warrantyStatus ?? "")
                              ? intl.formatMessage({ id: "intl-msg:monitoring.warrantyStatus.inWarranty" })
                              : [
                                "Expirado",
                                "Fora de Garantia",
                                "Fora de Garantía",
                                "Out of Warranty",
                                "Fuera de garantia",
                                "Fuera de garantía",
                                "Expired",
                              ].includes(asset.warrantyStatus ?? "")
                                ? intl.formatMessage({ id: "intl-msg:monitoring.warrantyStatus.outOfWarranty" })
                                : asset.warrantyStatus}
                          </span>
                        </div>
                      )}

                      {column.value === "volksTotal" && (
                        <div className="volks-total text-center">
                          {asset.hasTruckerPayPlan ? (
                            <button
                              type="button"
                              className="btn btn-link p-0 text-color-gray text-decoration-underline"
                              onClick={() => {
                                setContractDetailsAsset(asset);
                                setShowContractDetailsDialog(true);
                              }}
                            >
                              Trucker|Pay
                            </button>
                          ) : asset.volksTotalPlan !== "" ? (
                            <span className="text-color-gray">
                              {asset.volksTotalPlan}
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={() => window.open("https://latam-marketplace.rio.cloud/customerCenter/services", "_blank")}
                            >
                              <span
                                className="rioglyph rioglyph-shopping-cart"
                                aria-hidden="true"
                              ></span>
                            </button>
                          )}
                        </div>
                      )}

                      {column.value === "ultimaRevisao" && (() => {
                        const lastReview = getLastReview(asset);
                        return (
                          <div className="ultima-revisao">
                            {lastReview ? (
                              <div
                                className="display-flex flex-column"
                                key={assetIndex}
                              >
                                <span className="flex items-center justify-between">
                                  {lastReview.checkup?.name}
                                  
                                </span>
                                <span className="text-color-info">
                                  {`Realizada em ${lastReview.date?.toLocaleDateString("pt-BR")}`}
                                </span>
                              </div>
                            ) : (
                              <div className="text-center">
                                <span className="text-color-secondary">-</span>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {column.value === "proximaRevisao" && (
                        <div className="proxima-revisao">
                          <NextScheduling
                            asset={asset}
                            hasMetrics={assetHasMetrics(
                              asset,
                              props.uptimeAssets
                            )} />
                        </div>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {renderModals()}
      </div>
    );
  }


  const desktopRender = () => (

    <div id="TableCommonDemo">
      <TableToolbar>
        <div className="table-toolbar-container">
          <div className="table-toolbar-group-right">
            <div className="table-toolbar-column table-toolbar-column-spacer">
              <div className="flex gap-10">
                <button
                  type="button"
                  className="btn bg-highlight-darker padding-7"
                  onClick={() =>
                    navigate(`/dealership/appointments/appointment`)
                  }
                >
                  <span className="text-capitalize padding-right-5">
                    {intl.formatMessage({ id: "intl-msg:monitoring.createScheduling" })}
                  </span>
                  <span
                    className="rioglyph rioglyph-plus-sign"
                    aria-hidden="true"
                  />
                </button>

                {/*<button
                  className="btn bg-highlight-darker flex items-center justify-center"
                  onClick={exportToExcel}
                  disabled={!props.assets?.length}
                >
                  <span className="rioglyph rioglyph-download !text-[20px] !m-[0]"></span>
                </button>*/}
              </div>
            </div>
          </div>
        </div>
      </TableToolbar>

      {/* Sticky Header Fixo */}
      {showStickyHeader && (
        <div
          className="fixed  tables-row sticky-header"
          style={{
            top: '50px',
            zIndex: 6,
            backgroundColor: '#f8f9fa',
            transition: 'opacity 0.1s ease-out',
            opacity: 1,
            left: fixedTableContainerRef.current?.getBoundingClientRect().left ?? 0,
            width: fixedTableWidth > 0 ? fixedTableWidth : getStickyHeaderWidth(windowWidth, props.isSidebarOpen),
          }}
        >
          <div className="sticky-header-fixed">
            <table className="table table-layout-fixed table-bordered">
              <colgroup>
                {fixedColumns.map((column) =>
                  renderTableCaption(column.value, column.details)
                )}
              </colgroup>
              <thead>
                <tr>
                  {fixedColumns.map((column) =>
                    renderTableHead(
                      column.value,
                      column.label,
                      "veiculo",
                      () => {}
                    )
                  )}
                </tr>
              </thead>
            </table>
          </div>
        </div>
      )}

      {/* Sticky Header Scrollable */}
      {renderStickyScrollableHeader()}

      <div className="tables-row">
        <div className="tables-row__fixed" ref={fixedTableContainerRef}>
          <table className="table table-layout-fixed table-bordered">
            <colgroup>
              {fixedColumns.map((column) =>
                renderTableCaption(column.value, column.details)
              )}
            </colgroup>
            <thead ref={fixedTableHeaderRef}>
              <tr>
                {fixedColumns.map((column) =>
                  renderTableHead(
                    column.value,
                    column.label,
                    "veiculo",
                    () => {}
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {props.assets.map((asset: Asset, assetIndex: number) => (
                <tr key={assetIndex}>
                  {fixedColumns.map((column, colIndex) => (
                    <td
                      key={`${column.value}-${assetIndex}-${colIndex}`}
                      data-field={column.label}
                    >
                      {renderSchedulingData(column.value, asset)}

                      {column.value === "veiculo" && (
                        <div className="warning-vehicle">
                          <span className="vehicle-name" title={asset.name}>
                            {asset.name ?? asset.model}
                          </span>
                          {hasCriticalAlert(asset.id) && (
                            <SimpleTooltip content={`${getCriticalAlertCount(asset.id)} alerta(s) crítico(s)`}>
                              <span
                                className="rioglyph rioglyph-warning-sign text-color-danger cursor-pointer"
                                style={{ fontSize: '18px' }}
                                onClick={() => handleAlertSidebar(asset.id)}
                              />
                            </SimpleTooltip>
                          )}
                          {!hasCriticalAlert(asset.id) && hasAlert(asset.id) ? (
                            <button
                              type="button"
                              className="btn btn-secondary btn-action text-size-h6"
                              onClick={() => handleAlertSidebar(asset.id)}
                            >
                              <span
                                className="rioglyph rioglyph-warning-sign"
                                aria-hidden="true"
                              ></span>
                            </button>
                          ) : null}
                        </div>
                      )}

                      {renderChassisData(column.value, asset)}
                      {renderOdometerData(column.value, asset)}
                      {renderHourmeterData(column.value, asset)}

                      {column.value === "licensePlate" && (
                        <div>
                          {asset.licensePlate === '-' ? (
                            <span>-</span> 
                          ) : asset.licensePlate ? (
                            <SimpleTooltip
                              content={asset.licensePlate}
                              placement="top"
                            >
                              <div className="truncate">{asset.licensePlate}</div>
                            </SimpleTooltip>
                          ) : (
                            <div className="text-center text-color-light">
                              <span className="text-sm">Não informado</span>
                            </div>
                          )}
                        </div>
                      )}

                      {column.value === "accountName" &&
                        (asset.accountName ? (
                          <SimpleTooltip
                            content={asset.accountName}
                            placement="top"
                          >
                            <div className="line-clamp-3">
                              {asset.accountName}
                            </div>
                          </SimpleTooltip>
                        ) : (
                          <div className="animate-pulse">
                            <div className="w-3/4 h-4 bg-gray-300 rounded" />
                          </div>
                        ))}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div
          className={`scrollabe-table original-scrollable-table ${scrolledClass ? "scrolled-ativo" : ""}`}
          ref={scrollableTableRef}
          data-scrolled={scrolledClass}
          style={{
            overflowX: 'scroll',
            scrollbarWidth: 'none',
            width: '100%',
            marginLeft: fixedTableWidth > 0 ? fixedTableWidth + 8 : getScrollableTableMargin(windowWidth, props.isSidebarOpen)
          }}
        >
          <table
            className="table table-layout-fixed table-column-overflow-hidden table-bordered table-head-filled"
            style={{
              minWidth: `${scrollableColumns.reduce((sum, col) => sum + (col.details?.width || col.details?.defaultWidth || 100), 0)}px`,
              width: `${scrollableColumns.reduce((sum, col) => sum + (col.details?.width || col.details?.defaultWidth || 100), 0)}px`,
              tableLayout: 'fixed'
            }}
          >
            <colgroup>
              {scrollableColumns.map((column) =>
                renderTableCaption(column.value, column.details)
              )}
            </colgroup>
            <thead ref={scrollableTableHeaderRef}>
              <tr>
                {scrollableColumns.map((column) => {
                  const colWidth = column.details?.width || column.details?.defaultWidth || 100;
                  return (
                    <th
                      key={column.value}
                      data-column={column.value}
                      className="user-select-none sort-column"
                      data-field={column.value}
                      title={column.label}
                      style={{ 
                        width: `${colWidth}px`,
                        minWidth: `${colWidth}px`,
                        maxWidth: `${colWidth}px`,
                        textAlign: column.value === "rio" ? "center" : undefined 
                      }}
                    >
                      <span>
                        <SortArrows />
                        <span>{column.label}</span>
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {props.assets.map((asset: Asset, assetIndex: number) => (
                <tr key={assetIndex}>
                  {scrollableColumns.map((column) => {
                    const colWidth = column.details?.width || column.details?.defaultWidth || 100;
                    return (
                      <td
                        key={column.value}
                        data-column={column.value}
                        style={{
                          width: `${colWidth}px`,
                          minWidth: `${colWidth}px`,
                          maxWidth: `${colWidth}px`,
                          verticalAlign: 'middle'
                        }}
                      >
                      {renderChassisData(column.value, asset)}
                      {renderOdometerData(column.value, asset)}
                      {renderHourmeterData(column.value, asset)}

                      {column.value === "acoes" ? (
                        <div className="acoes-oficina text-center display-flex items-center justify-center gap-2">
                          {hasCampaign(asset) ? (
                            <div className="cursor-pointer relative" onClick={() => handleCampaignsSidebar(asset)}>
                              <span className="rioglyph rioglyph-calendar-today" />
                              {(asset?.campaigns?.number ?? 0) > 0 && (
                                <span className="alert-info">
                                  {asset?.campaigns?.number}
                                </span>
                              )}
                            </div>
                          ) : (
                            <div>
                              <span className="rioglyph rioglyph-calendar-today" />
                            </div>
                          )}
                        </div>
                      ) : null}

                      {column.value === "revisoes" &&
                        (asset.schedulings?.previous ? (
                          <div className="revisoes-periodicas text-center">
                            <button
                              type="button"
                              className="btn btn-primary btn-action text-size-22"
                              onClick={() => handlePeriodicReviewSidebar(asset)}
                            >
                              <span
                                className="rioglyph rioglyph-book"
                                aria-hidden="true"
                              ></span>
                            </button>
                          </div>
                        ) : (
                          <div className="animate-pulse text-center">
                            <div className="w-3/4 h-4 bg-gray-300 rounded" />
                          </div>
                        ))}

                      {column.value === "score" && (
                        <div className="text-center text-color-light">-</div>
                      )}

                      {column.value === "grupoManutencao" && (
                        <div className="text-center">{asset.groupName}</div>
                      )}

                      {column.value === "status" && (
                        <div className="truncate">
                          <span
                            className={
                              ["Online", "Conectado"].includes(
                                props.assetsConnectionStatus.find(
                                  (el) => el.assetId === asset.id
                                )?.connection ?? ""
                              )
                                ? "rioglyph rioglyph-wifi text-color-status-available padding-right-5"
                                : ["Offline", "Desconectado"].includes(
                                    props.assetsConnectionStatus.find(
                                      (el) => el.assetId === asset.id
                                    )?.connection ?? ""
                                  )
                                ? "rioglyph rioglyph-wifi-off padding-right-5"
                                : undefined
                            }
                          ></span>
                          {["Online", "Conectado"].includes(
                            props.assetsConnectionStatus.find(
                              (el) => el.assetId === asset.id
                            )?.connection ?? ""
                          )
                            ? intl.formatMessage({ id: "intl-msg:monitoring.status.connected" })
                            : intl.formatMessage({ id: "intl-msg:monitoring.status.notConnected" })}
                        </div>
                      )}

                      {column.value === "rio" && renderRioPlan(asset)}

                      {column.value === "statusGarantia" && (
                        <div className="status-garantia display-grid place-items-center">
                          <span
                            className={
                              [
                                "Em Garantia",
                                "In Warranty",
                                "En garantia",
                                "En garantía",
                              ].includes(asset.warrantyStatus ?? "")
                                ? "text-color-garantia"
                                : "text-color-expirada"
                            }
                          >
                            {[
                              "Em Garantia",
                              "In Warranty",
                              "En garantia",
                              "En garantía",
                            ].includes(asset.warrantyStatus ?? "")
                              ? intl.formatMessage({ id: "intl-msg:monitoring.warrantyStatus.inWarranty" })
                              : [
                                "Expirado",
                                "Fora de Garantia",
                                "Fora de Garantía",
                                "Out of Warranty",
                                "Fuera de garantia",
                                "Fuera de garantía",
                                "Expired",
                              ].includes(asset.warrantyStatus ?? "")
                                ? intl.formatMessage({ id: "intl-msg:monitoring.warrantyStatus.outOfWarranty" })
                                : asset.warrantyStatus}
                          </span>
                        </div>
                      )}

                      {column.value === "volksTotal" && (
                        <div className="volks-total text-center">
                          {asset.hasTruckerPayPlan ? (
                            <button
                              type="button"
                              className="btn btn-link p-0 text-color-gray text-decoration-underline"
                              onClick={() => {
                                setContractDetailsAsset(asset);
                                setShowContractDetailsDialog(true);
                              }}
                            >
                              Trucker|Pay
                            </button>
                          ) : asset.volksTotalPlan !== "" ? (
                            <span className="text-color-gray">
                              {asset.volksTotalPlan}
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={() =>
                                window.open(
                                  "https://latam-marketplace.rio.cloud/customerCenter/services",
                                  "_blank"
                                )
                              }
                            >
                              <span
                                className="rioglyph rioglyph-shopping-cart"
                                aria-hidden="true"
                              ></span>
                            </button>
                          )}
                        </div>
                      )}

                      {column.value === "ultimaRevisao" && (() => {
                        const lastReview = getLastReview(asset);
                        return (
                          <div className="ultima-revisao" style={{ height: '60px', overflow: 'hidden' }}>
                            {lastReview ? (
                              <div
                                className="display-flex flex-column"
                                key={assetIndex}
                              >
                                <span className="flex items-center justify-between">
                                  {lastReview.checkup?.name}
                                  <span
                                    className="rioglyph-ok-circle text-color-status-available"
                                  ></span>
                                </span>
                                <span className="text-color-info">
                                  {`Realizada em ${lastReview.date?.toLocaleDateString("pt-BR")}`}
                                </span>
                              </div>
                            ) : (
                              <div className="text-center">
                                <span className="text-color-secondary">-</span>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {column.value === "proximaRevisao" && (
                        <div className="proxima-revisao" style={{ height: '60px', overflow: 'hidden' }}>
                          <NextScheduling
                            asset={asset}
                            hasMetrics={assetHasMetrics(
                              asset,
                              props.uptimeAssets
                            )}
                          />
                        </div>
                      )}
                    </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>

  );
  return (
    <>
      {desktopRender()}
      {renderModals()}
    </>
  )
};

export default MaintenanceTable;
